<template>
  <header class="cabecalho">
    <div class="cabecalho__interior">
      <!--
        3.6 — O EMBLEMA LEVA PARA O CAIS.

        "Início sai da barra: o emblema do Fishbay no topo leva para casa, convenção que todo mundo
        já conhece" (D12). É o que libera a sexta vaga da barra sem perder o destino.
      -->
      <router-link to="/home" class="cabecalho__emblema" :aria-label="$t('footer.homeTooltip')">
        <img src="/logotipos/logo-fishbay.png" alt="Fishbay" class="cabecalho__marca" />
      </router-link>

      <FishHud
        class="cabecalho__hud"
        :energia="energia"
        :energia-maxima="energiaMaxima"
        :proxima-recarga-em="proximaRecargaEm"
        :recarga-por-tick="recargaPorTick"
        :ouro="ouro"
        :parallelium="parallelium"
        :nivel="nivel"
        :experiencia="experiencia"
        :experiencia-maxima="experienciaMaxima"
        :nome="nome"
        mostrar-parallelium
        :compacto="estreito"
        @perfil="irParaPerfil"
        @parallelium="$emit('open-paralleliums')"
        @ouro="irParaLoja"
      />
    </div>
  </header>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import FishHud from '@/shared/ui/FishHud.vue';

/**
 * 3.7 — o header enxuto (UI-16, D6, 7.3).
 *
 * ## O corte
 *
 * O header tinha **seis elementos competindo**: nível, XP, energia, ouro, parallelium e engrenagem.
 * Agora tem **três**, mais o emblema:
 *
 * - **energia**, porque é o que limita a próxima ação;
 * - **ouro**, porque é a restrição de tudo o que se compra;
 * - **perfil**, porque é a porta para o resto — e **o nível mora dentro dele** agora.
 *
 * **XP saiu.** Ele é orgulho, não decisão: ninguém escolhe o que fazer olhando a barra de
 * experiência. Ele vive no perfil (D29), junto do resto da vitrine.
 *
 * **A engrenagem saiu** e foi para a folha "Mais" (D12), junto de Configurações.
 *
 * ## O que sumiu de código, e é o ponto do UI-16
 *
 * Havia **duas** implementações do mesmo cabeçalho — `Header.vue` (desktop) e `MobileHeader.vue`
 * (telefone) —, cada uma com sua barra de XP, sua pílula de ouro e seu formatador de número. Agora
 * há **um** `FishHud`, e ele se adapta pela prop `compacto`. Duas implementações da mesma coisa é o
 * mecanismo exato pelo qual a Loja, Melhorias e o Inventário acabaram desenhando cada um a sua
 * própria pílula de ouro.
 *
 * O `MobileHeader.vue` e o `StaminaBar.vue` saíram do repositório em R3 ("11 componentes mortos
 * saem") — a energia passou a viver dentro do `FishHud`.
 *
 * ## O que foi junto, e custou caro (04/09/2026)
 *
 * O `StaminaBar.vue` não desenhava só a barra: ele era **o relógio da energia**. Tinha um
 * `setInterval` de um minuto e reagia ao `nextRechargeIn` que o servidor devolvia, e era ele que
 * fazia o número subir na tela. O `FishHud` herdou o desenho e não o relógio, então a barra
 * parou — a energia continuava regenerando no servidor e ninguém relia. Apagar componente morto
 * estava certo; o que faltou foi reparar que aquele não era só desenho.
 *
 * O relógio voltou, e **fora de qualquer componente**: mora em `player/iniciarRelogioDaEnergia` e
 * o `App.vue` o liga. O HUD aparece em todas as telas, então quem relê tem que viver acima delas.
 *
 * ## O parallelium fica SEMPRE no HUD
 *
 * O D6 propunha mostrá-lo só onde se gasta; **o autor vetou em 02/09/2026** ("cadê os Paralleliums
 * no header?") — é a moeda comprada com dinheiro real, e sumir com ela lia como o jogo tendo
 * perdido o saldo. Decisão de produto vence regra de layout.
 */
export default {
  name: 'GameHeader',
  components: { FishHud },
  emits: ['open-configs', 'open-paralleliums'],
  data() {
    return { estreito: false };
  },
  computed: {
    ...mapState('player', { energiaMaxima: (state) => state.stamina?.max || 30 }),
    ...mapGetters('player', {
      nivel: 'getLevel',
      ouro: 'getGold',
      parallelium: 'getParalleliums',
    }),
    energia() {
      return this.$store.state.player?.stamina?.current ?? 0;
    },
    /** O instante do próximo ponto, carimbado pela store na leitura do perfil. */
    proximaRecargaEm() {
      return this.$store.state.player?.stamina?.nextRechargeAt ?? 0;
    },
    recargaPorTick() {
      return this.$store.state.player?.stamina?.rechargeStamina || 1;
    },
    experiencia() {
      return this.$store.state.player?.experience ?? 0;
    },
    experienciaMaxima() {
      return this.$store.state.player?.maxExperience ?? 0;
    },
    nome() {
      return this.$store.state.auth?.user?.username || 'Pescador';
    },
  },
  mounted() {
    this.medir();
    window.addEventListener('resize', this.medir, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.medir);
  },
  methods: {
    medir() {
      // 600px é o breakpoint `tablet` dos quatro (UI-08). Antes eram dois componentes inteiros
      // trocados a 768px; agora é uma prop.
      this.estreito = !window.matchMedia('(min-width: 600px)').matches;
    },
    irParaPerfil() {
      const nome = this.$store.state.auth?.user?.username;
      this.$router.push(nome ? `/profile/${nome}` : '/inventory');
    },
    irParaLoja() {
      this.$router.push('/shop');
    },
  },
};
</script>

<style scoped>
/**
 * O cabeçalho é uma **viga de madeira**, não uma faixa de cor.
 *
 * A primeira versão era um retângulo marrom chapado com dois elementos pequenos nas pontas e um
 * vazio enorme no meio — que é exatamente o "parece painel administrativo" que o redesenho existe
 * para consertar. Três coisas resolvem, e nenhuma delas é encher o meio:
 *
 * 1. **A viga tem veio.** As faixas do `repeating-linear-gradient` são as tábuas; sem elas, madeira
 *    é só uma cor marrom.
 * 2. **Ela tem espessura.** Luz na aresta de cima, sombra na de baixo — o mesmo bisel do botão. É o
 *    que faz a viga estar SOBRE a tela em vez de pintada nela.
 * 3. **O fio de latão embaixo** separa a viga do jogo, e é a única linha brilhante do cabeçalho.
 */
.cabecalho {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-cabecalho);

  /* A viga é ESCURA de propósito: numa tela de 1920px ela é uma massa enorme, e em --madeira
   * (clara) ela dominava a tela e lia como lajota amarela — conferido em screenshot. Escura, ela
   * recua e vira palco. As duas cadências de emenda (96 e 152px) se sobrepõem para as tábuas não
   * caírem numa grade mecânica. */
  background: var(--madeira-sombra);
  background-image: repeating-linear-gradient(90deg, rgba(0, 0, 0, 0.16) 0 1px, transparent 1px 96px),
    repeating-linear-gradient(90deg, rgba(0, 0, 0, 0.11) 0 1px, transparent 1px 152px);
  border-bottom: 2px solid var(--latao);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), inset 0 -4px 8px rgba(0, 0, 0, 0.3),
    0 2px 0 rgba(0, 0, 0, 0.35);
  padding-top: env(safe-area-inset-top, 0px);
}

.cabecalho__interior {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  /* O HUD é ancorado à DIREITA e o emblema à esquerda. Numa tela larga o vazio no meio é
   * intencional: o D6 corta o cabeçalho a três elementos, e preenchê-lo desfaria o corte. */
  justify-content: space-between;
  max-width: 1100px;
  margin: 0 auto;
  padding: var(--e-2) var(--e-3);
}

.cabecalho__emblema {
  flex-shrink: 0;
  line-height: 0;
  padding: var(--e-1);
  transition: transform var(--t-rapido) var(--curva-quique);
}

.cabecalho__emblema:active {
  transform: scale(0.94);
}

.cabecalho__marca {
  height: 40px;
  width: auto;
  image-rendering: pixelated;
  /* A sombra dura de 2px prega o emblema na viga. Sem ela ele flutua, e um logotipo flutuando é a
   * diferença entre "emblema pendurado" e "PNG colado". */
  filter: drop-shadow(0 2px 0 rgba(0, 0, 0, 0.5));
}

@media (min-width: 600px) {
  .cabecalho__marca {
    height: 48px;
  }
}

/* No telefone a viga devolve 12px ao HUD — cada pixel vira número de ouro legível (28/09/2026,
 * ver `.fish-hud--compacto`). O emblema continua com 40px de arte: é o alvo do "voltar ao cais". */
@media (max-width: 599px) {
  .cabecalho__interior {
    gap: var(--e-2);
    padding: var(--e-2);
  }

  .cabecalho__emblema {
    padding: 2px;
  }
}

.cabecalho__hud {
  justify-content: flex-end;
  min-width: 0;
}
</style>
